import React, { useState, useEffect } from 'react';
import { isStaff } from './utils/permissions';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ToastCard from './components/Common/ToastCard';
import { useAuth } from './context/AuthContext';

// Pages
import RegisterPage from './pages/Auth/RegisterPage';
import InvitePage from './pages/InvitePage';
import ReferralPage from './pages/Referral/ReferralPage';

// Admin Pages
import DashboardPage from './pages/Admin/DashboardPage';
import CourseInnerPage from './pages/Admin/CourseInnerPage';
import SectionsPage from './pages/Admin/SectionsPage';
import HomeworkPage from './pages/Admin/HomeworkPage';
import ReviewPage from './pages/Admin/ReviewPage';
import StudentsPage from './pages/Admin/StudentsPage';
import ProgressPage from './pages/Admin/ProgressPage';
import AdminVariantsPage from './pages/Admin/AdminVariantsPage';
import AdminVariantEditPage from './pages/Admin/AdminVariantEditPage';
import AdminProfilePage from './pages/Admin/AdminProfilePage';
import AdminStaffPage from './pages/Admin/AdminStaffPage';
import AdminAccessPage from './pages/Admin/AdminAccessPage';

// Student Pages
import HomePage from './pages/Student/HomePage';
import LessonPage from './pages/Student/LessonPage';
import LessonsPage from './pages/Student/LessonsPage';
import StudentHomeworkPage from './pages/Student/StudentHomeworkPage';
import LessonHomeworkPage from './pages/Student/LessonHomeworkPage';
import VariantPrintPage from './pages/Student/VariantPrintPage';
import MaterialsPage from './pages/Student/MaterialsPage';
import TestsPage from './pages/Student/TestsPage';
import StudentProgressPage from './pages/Student/StudentProgressPage';
import MistakesPage from './pages/Student/MistakesPage';
import ProfilePage from './pages/Student/ProfilePage';
import WaitingPage from './pages/Student/WaitingPage';

// Public
import LandingPage from './pages/Landing/LandingPage';
import { PrivacyPage, ConsentPage, TermsPage } from './pages/Legal/LegalPages';

// Layouts
import AdminLayout from './components/Layout/AdminLayout';
import StudentLayout from './components/Layout/StudentLayout';

import { ConfirmHost } from './components/Common/confirm';
import { ResetPasswordPage } from './pages/Auth/AuthActionPages';

// Styles
import './App.css';
// Liquid glass — подключаем ПОСЛЕДНИМ, чтобы перекрыть Admin.css/Student.css
import './styles/liquid-glass.css';

function AppRoutes() {
  const { user, loading } = useAuth();
  // Плавная загрузка: держим лоадер, пока не подгрузятся бренд-шрифты,
  // чтобы текст не «прыгал» (FOUT) и всё проявлялось разом.
  const [assetsReady, setAssetsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setAssetsReady(true); };

    // Искусственный показ анимации: ?boot или ?boot=5 (секунды)
    const params = new URLSearchParams(window.location.search);
    const bootDelay = params.has('boot') ? (parseInt(params.get('boot'), 10) || 4) * 1000 : 0;
    const start = Date.now();

    const fontsP = (document.fonts && document.fonts.load)
      ? Promise.all([
          document.fonts.load('400 1em "Saar SP Demo"'),
          document.fonts.load('400 1em "Montserrat"'),
          document.fonts.load('500 1em "Montserrat"'),
          document.fonts.load('700 1em "Montserrat"'),
        ]).then(() => document.fonts.ready).catch(() => {})
      : Promise.resolve();

    fontsP.then(() => {
      const wait = Math.max(bootDelay - (Date.now() - start), 0);
      setTimeout(reveal, wait);
    });
    // Подстраховка: не зависнуть, если шрифты не догрузились
    const fallback = setTimeout(reveal, Math.max(2800, bootDelay + 400));
    return () => { cancelled = true; clearTimeout(fallback); };
  }, []);

  // Панель браузера на телефоне (над страницей) — в цвет страницы, а не
  // фиолетовая: иначе верх страницы резко обрывается о цветную полосу.
  // Следим за классом body: тему переключает и useTheme, и лендинг.
  // Пока висит загрузочная заставка — цвет заставки, иначе на её фоне
  // тёмная панель браузера читалась как граница.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta || typeof MutationObserver === 'undefined') return;
    const sync = () => {
      const splash = document.getElementById('boot-splash');
      const color = splash && !splash.classList.contains('gone') ? '#2B1466'
        : document.body.classList.contains('dark') ? '#0d0a16' : '#efebfb';
      if (meta.getAttribute('content') !== color) meta.setAttribute('content', color);
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
    const splash = document.getElementById('boot-splash');
    if (splash) mo.observe(splash, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);

  // Готовы — плавно снимаем HTML-заставку из index.html
  useEffect(() => {
    if (loading || !assetsReady) return;
    const el = document.getElementById('boot-splash');
    if (!el) return;
    el.classList.add('gone');
    const t = setTimeout(() => el.remove(), 320);
    return () => clearTimeout(t);
  }, [loading, assetsReady]);

  // Пока грузимся — ничего не рисуем: сверху всё ещё висит HTML-заставка из
  // index.html, и её анимация не прерывается. Снимаем её, когда готовы.
  if (loading || !assetsReady) return null;

  return (
    <div className="app-reveal">
    <Routes>
      {/* Public routes */}
      {/* Гостевой экран входа — это лендинг на "/". Старый /login ведёт туда же. */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/invite" element={<InvitePage />} />
      {/* Приглашение друга по реферальной программе: /r/Имя-Фамилия */}
      <Route path="/r/:who" element={<ReferralPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/consent" element={<ConsentPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Публичный просмотр успеваемости ученика по коду (для родителей) */}
      <Route path="/p" element={<LandingPage />} />
      <Route path="/p/:code" element={<LandingPage />} />

      {/* Admin routes */}
      <Route path="/admin" element={
        isStaff(user?.role) ? <AdminLayout /> : <Navigate to="/" />
      }>
        <Route index element={<Navigate to="/admin/dashboard" />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="course/:id" element={<CourseInnerPage />} />
        <Route path="sections" element={<SectionsPage />} />
        <Route path="homework" element={<HomeworkPage />} />
        <Route path="review" element={<ReviewPage />} />
        <Route path="students" element={<StudentsPage />} />
        <Route path="progress" element={<ProgressPage />} />
        <Route path="variants" element={<AdminVariantsPage />} />
        <Route path="variants/:id" element={<AdminVariantEditPage />} />
        <Route path="staff" element={<AdminStaffPage />} />
        <Route path="access" element={<AdminAccessPage />} />
        <Route path="profile" element={<AdminProfilePage />} />
      </Route>

      {/* Бланк варианта на печать — отдельным окном, без сайдбара */}
      <Route path="/variant/:lessonId/print" element={
        user?.role === 'student' ? <VariantPrintPage /> : <Navigate to="/" replace />
      } />

      {/* Ученик без курса — экран ожидания зачисления */}
      <Route path="/waiting" element={
        user?.role === 'student' && !user?.course ? <WaitingPage />
        : <Navigate to="/" replace />
      } />

      {/* Root: лендинг для гостей, кабинет для студента, редирект для админа.
          Ученик без активного курса отправляется на экран ожидания. */}
      <Route path="/" element={
        user?.role === 'student' ? (user?.course ? <StudentLayout /> : <Navigate to="/waiting" replace />)
        : isStaff(user?.role) ? <Navigate to="/admin" />
        : <LandingPage />
      }>
        <Route index element={<Navigate to="/home" />} />
        <Route path="home" element={<HomePage />} />
        <Route path="lessons" element={<LessonsPage />} />
        <Route path="homework" element={<StudentHomeworkPage />} />
        <Route path="homework/:lessonId" element={<LessonHomeworkPage />} />
        <Route path="variant/:lessonId" element={<LessonHomeworkPage />} />
        <Route path="materials" element={<MaterialsPage />} />
        <Route path="tests" element={<TestsPage />} />
        <Route path="progress" element={<StudentProgressPage />} />
        <Route path="mistakes" element={<MistakesPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="lesson/:id" element={<LessonPage />} />
        {/* Legacy redirects */}
        <Route path="course" element={<Navigate to="/home" />} />
      </Route>

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
      <ConfirmHost />
      {/* Логи — стеклянные плашки (дизайн 1c). Рисуем своим компонентом,
          поэтому toast.success / error / loading работают как прежде. */}
      <Toaster position="top-right" gutter={10} toastOptions={{ duration: 3200 }}>
        {(t) => <ToastCard t={t} />}
      </Toaster>
    </AuthProvider>
  );
}

export default App;
